import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    ArrowLeft,
    Settings,
    Users,
    Mail,
    Trash2,
    Loader2,
    Copy,
    Check,
    Crown,
    Shield,
    User,
    Eye,
    Upload,
    X,
    Image as ImageIcon,
    Webhook,
    Database,
    Download,
    AlertTriangle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import {
    useProject,
    useUpdateProject,
    useDeleteProject,
    useProjectMembers,
    useUpdateMemberRole,
    useRemoveMember,
    useProjectInvitations,
    useCreateInvitation,
    useCancelInvitation,
    useProjectJoinLink,
    useGenerateProjectJoinLink,
    useDisableProjectJoinLink,
    useProjectDiscordWebhook,
    useSaveProjectDiscordWebhook,
    useTestProjectDiscordWebhook,
    useDeleteProjectDiscordWebhook,
    useExportProjectTasks,
    usePreviewProjectTaskImport,
    useImportProjectTasks,
} from '@/hooks/use-projects';
import { InviteLinkList } from '@/components/invite-link-list';
import { InviteLinkDialog } from '@/components/invite-link-dialog';
import { useCreateInviteLink, type ProjectInviteLink } from '@/hooks/use-invite-links';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { CustomFieldManager } from '@/components/tasks';
import {
    updateProjectSchema,
    createInvitationSchema,
    type UpdateProjectInput,
    type CreateInvitationInput,
    type ProjectRole,
    type ProjectTransferDocument,
    type ProjectImportSummary,
} from '@/lib/schemas';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import { AppLayout } from '@/components/layout';

import type { Translations } from '@/lib/i18n';

// Preset colors for project
const PRESET_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
];

// Preset emojis for project
const PRESET_EMOJIS = ['📁', '📋', '🚀', '💡', '🎯', '⭐', '🔥', '💻', '📊', '🎨'];

const getRoleLabel = (role: ProjectRole, t: (key: keyof Translations) => string) => {
    const labels: Record<ProjectRole, string> = {
        OWNER: t('projects.owner'),
        ADMIN: t('projects.admin'),
        MEMBER: t('projects.member'),
        VIEWER: t('projects.viewer'),
    };
    return labels[role];
};

const roleIcons: Record<ProjectRole, typeof Crown> = {
    OWNER: Crown,
    ADMIN: Shield,
    MEMBER: User,
    VIEWER: Eye,
};

const roleBadgeColors: Record<ProjectRole, string> = {
    OWNER: 'bg-amber-500/20 text-amber-400',
    ADMIN: 'bg-purple-500/20 text-purple-400',
    MEMBER: 'bg-blue-500/20 text-blue-400',
    VIEWER: 'bg-gray-500/20 text-gray-400',
};

type IconType = 'emoji' | 'image';

export function ProjectSettingsPage() {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { toast } = useToast();
    const { t } = useI18n();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const importFileInputRef = useRef<HTMLInputElement>(null);

    const [activeTab, setActiveTab] = useState<'general' | 'members' | 'integrations' | 'data'>('general');
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [copiedInvite, setCopiedInvite] = useState<string | null>(null);
    const [selectedColor, setSelectedColor] = useState<string>('');
    const [selectedEmoji, setSelectedEmoji] = useState<string>('');
    const [iconType, setIconType] = useState<IconType>('emoji');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [discordWebhookUrl, setDiscordWebhookUrl] = useState('');
    const [importDocument, setImportDocument] = useState<ProjectTransferDocument | null>(null);
    const [importPreview, setImportPreview] = useState<ProjectImportSummary | null>(null);
    const [importFileName, setImportFileName] = useState('');
    const [showImportPreview, setShowImportPreview] = useState(false);

    const { data: project, isLoading: projectLoading } = useProject(projectId || '');
    const { data: members } = useProjectMembers(projectId || '');
    const { data: invitations } = useProjectInvitations(projectId || '');
    const { data: joinLink } = useProjectJoinLink(projectId || '');
    const { data: discordWebhook } = useProjectDiscordWebhook(projectId || '');

    const updateProject = useUpdateProject(projectId || '');
    const deleteProject = useDeleteProject();
    const updateMemberRole = useUpdateMemberRole(projectId || '');
    const removeMember = useRemoveMember(projectId || '');
    const createInvitation = useCreateInvitation(projectId || '');
    const cancelInvitation = useCancelInvitation(projectId || '');
    const generateJoinLink = useGenerateProjectJoinLink(projectId || '');
    const disableJoinLink = useDisableProjectJoinLink(projectId || '');
    const saveDiscordWebhook = useSaveProjectDiscordWebhook(projectId || '');
    const testDiscordWebhook = useTestProjectDiscordWebhook(projectId || '');
    const deleteDiscordWebhook = useDeleteProjectDiscordWebhook(projectId || '');
    const exportProjectTasks = useExportProjectTasks(projectId || '');
    const previewProjectImport = usePreviewProjectTaskImport(projectId || '');
    const importProjectTasks = useImportProjectTasks(projectId || '');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<UpdateProjectInput>({
        resolver: zodResolver(updateProjectSchema),
        values: project
            ? {
                name: project.name,
                description: project.description || '',
            }
            : undefined,
    });

    const {
        register: registerInvite,
        handleSubmit: handleInviteSubmit,
        reset: resetInvite,
        setValue: setInviteValue,
        watch: watchInvite,
        formState: { errors: inviteErrors },
    } = useForm<CreateInvitationInput>({
        resolver: zodResolver(createInvitationSchema),
        defaultValues: {
            email: '',
            role: 'MEMBER',
            expiresInDays: 7,
        },
    });

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Initialize color and emoji when project loads
    if (project && selectedColor === '') {
        setSelectedColor((project.color || PRESET_COLORS[0]) as string);
        setSelectedEmoji((project.iconEmoji || PRESET_EMOJIS[0]) as string);
        if (project.imageUrl) {
            setIconType('image');
            setImagePreview(project.imageUrl);
        }
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({
                title: t('toast.invalidFileType'),
                description: t('projects.invalidFileType'),
                variant: 'destructive',
            });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast({
                title: t('toast.fileTooLarge'),
                description: t('projects.fileTooLarge'),
                variant: 'destructive',
            });
            return;
        }

        setImageFile(file);
        setIconType('image');

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setIconType('emoji');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmitSettings = async (data: UpdateProjectInput) => {
        try {
            let imageUrl: string | null | undefined = undefined;

            // Convert image to base64 if a new file was selected
            if (iconType === 'image' && imageFile) {
                setIsUploading(true);
                try {
                    // Convert file to base64
                    imageUrl = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            resolve(reader.result as string);
                        };
                        reader.onerror = () => {
                            reject(new Error('Failed to read file'));
                        };
                        reader.readAsDataURL(imageFile);
                    });
                } catch (error) {
                    toast({
                        title: t('toast.uploadFailed'),
                        description: t('projects.invalidFileType'),
                        variant: 'destructive',
                    });
                    setIsUploading(false);
                    return;
                }
                setIsUploading(false);
            } else if (iconType === 'emoji' && project?.imageUrl) {
                // Clear image when switching to emoji
                imageUrl = null;
            }

            await updateProject.mutateAsync({
                ...data,
                color: selectedColor,
                iconEmoji: iconType === 'emoji' ? selectedEmoji : undefined,
                imageUrl: imageUrl || undefined,
            });
            toast({
                title: t('toast.settingsSaved'),
                description: t('toast.settingsSavedDesc'),
            });
            setImageFile(null); // Reset file after successful upload
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const onSubmitInvite = async (data: CreateInvitationInput) => {
        try {
            await createInvitation.mutateAsync(data);
            toast({
                title: t('toast.invitationSent'),
                description: t('toast.invitationSentDesc').replace('{email}', data.email),
            });
            resetInvite();
            setShowInviteDialog(false);
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleDelete = async () => {
        try {
            await deleteProject.mutateAsync(projectId || '');
            toast({
                title: t('toast.projectDeleted'),
                description: t('toast.projectDeletedDesc'),
            });
            navigate('/projects');
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleRoleChange = async (memberId: string, newRole: ProjectRole) => {
        try {
            await updateMemberRole.mutateAsync({ memberId, role: newRole });
            toast({
                title: t('toast.roleUpdated'),
                description: t('toast.roleUpdatedDesc'),
            });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleRemoveMember = async (memberId: string) => {
        try {
            await removeMember.mutateAsync(memberId);
            toast({
                title: t('toast.memberRemoved'),
                description: t('toast.memberRemovedDesc'),
            });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleCancelInvite = async (inviteId: string) => {
        try {
            await cancelInvitation.mutateAsync(inviteId);
            toast({
                title: t('toast.invitationCancelled'),
                description: t('toast.invitationCancelledDesc'),
            });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleSaveDiscordWebhook = async () => {
        if (!discordWebhookUrl.trim()) return;
        try {
            await saveDiscordWebhook.mutateAsync(discordWebhookUrl.trim());
            setDiscordWebhookUrl('');
            toast({ title: t('common.success'), description: t('projectSettings.webhookSaved') });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleTestDiscordWebhook = async () => {
        try {
            await testDiscordWebhook.mutateAsync();
            toast({ title: t('common.success'), description: t('projectSettings.webhookTestSent') });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleDeleteDiscordWebhook = async () => {
        try {
            await deleteDiscordWebhook.mutateAsync();
            toast({ title: t('common.success'), description: t('projectSettings.webhookRemoved') });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleExportTasks = async () => {
        try {
            const document = await exportProjectTasks.mutateAsync();
            const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const anchor = window.document.createElement('a');
            const projectName = project?.name || 'project';
            const safeName = projectName
                .normalize('NFKD')
                .replace(/[^a-zA-Z0-9_-]+/g, '-')
                .replace(/^-+|-+$/g, '') || 'project';
            anchor.href = url;
            anchor.download = `${safeName}-tasks-${new Date().toISOString().slice(0, 10)}.json`;
            window.document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (file.size > 25 * 1024 * 1024) {
            toast({
                title: t('common.error'),
                description: t('projectSettings.importFileTooLarge'),
                variant: 'destructive',
            });
            return;
        }

        try {
            const document = JSON.parse(await file.text()) as ProjectTransferDocument;
            if (document.format !== 'project-manager-task-export' || document.version !== 1) {
                throw new Error(t('projectSettings.invalidImportFile'));
            }
            const preview = await previewProjectImport.mutateAsync(document);
            setImportDocument(document);
            setImportPreview(preview);
            setImportFileName(file.name);
            setShowImportPreview(true);
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('projectSettings.invalidImportFile'),
                variant: 'destructive',
            });
        }
    };

    const handleConfirmImport = async () => {
        if (!importDocument) return;
        try {
            const result = await importProjectTasks.mutateAsync(importDocument);
            toast({
                title: t('projectSettings.importSuccess'),
                description: t('projectSettings.importSuccessDescription')
                    .replace('{created}', String(result.tasksCreated))
                    .replace('{updated}', String(result.tasksUpdated))
                    .replace('{deleted}', String(result.tasksDeleted)),
            });
            setShowImportPreview(false);
            setImportDocument(null);
            setImportPreview(null);
            setImportFileName('');
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const copyInviteLink = (url: string, id: string) => {
        navigator.clipboard.writeText(url);
        setCopiedInvite(id);
        setTimeout(() => setCopiedInvite(null), 2000);
        toast({
            title: t('toast.linkCopied'),
            description: t('toast.linkCopiedDesc'),
        });
    };

    const handleGenerateJoinLink = async () => {
        try {
            const generated = await generateJoinLink.mutateAsync();
            try {
                await navigator.clipboard.writeText(generated.joinUrl);
                setCopiedInvite('join-link');
                setTimeout(() => setCopiedInvite(null), 2000);
                toast({ title: t('toast.linkCopied'), description: t('toast.linkCopiedDesc') });
            } catch {
                toast({ title: t('common.success'), description: t('projectSettings.createJoinLink') });
            }
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleDisableJoinLink = async () => {
        try {
            await disableJoinLink.mutateAsync();
            toast({ title: t('common.success'), description: t('projectSettings.disableJoinLink') });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    if (projectLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                        {t('projectSettings.projectNotFound')}
                    </h2>
                    <Button onClick={() => navigate('/projects')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('projectSettings.backToProjects')}
                    </Button>
                </div>
            </div>
        );
    }

    const isOwner = project.role === 'OWNER';
    const canManage = project.role === 'OWNER' || project.role === 'ADMIN';

    if (!canManage) {
        navigate(`/projects/${projectId}`);
        return null;
    }

    return (
        <AppLayout>
            {/* Main content */}
            <div className="p-4 sm:p-6 md:p-8 min-w-0">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/projects/${projectId}`)}
                            className="shrink-0"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-3 min-w-0">
                            {project.imageUrl ? (
                                <img
                                    src={project.imageUrl}
                                    alt={project.name}
                                    className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover shrink-0"
                                />
                            ) : (
                                <div
                                    className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center text-lg sm:text-xl shrink-0"
                                    style={{ backgroundColor: project.color || '#6366f1' }}
                                >
                                    {project.iconEmoji || '📁'}
                                </div>
                            )}
                            <div className="min-w-0">
                                <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
                                    {t('projects.projectSettings')}
                                </h1>
                                <p className="text-sm text-foreground-muted truncate">{project.name}</p>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('general')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'general'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-foreground-muted hover:text-foreground'
                                }`}
                        >
                            <Settings className="h-4 w-4 inline mr-2" />
                            {t('settings.title')}
                        </button>
                        <button
                            onClick={() => setActiveTab('members')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'members'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-foreground-muted hover:text-foreground'
                                }`}
                        >
                            <Users className="h-4 w-4 inline mr-2" />
                            {t('projects.members')}
                        </button>
                        <button
                            onClick={() => setActiveTab('integrations')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'integrations'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-foreground-muted hover:text-foreground'
                                }`}
                        >
                            <Webhook className="h-4 w-4 inline mr-2" />
                            {t('projectSettings.integrations')}
                        </button>
                        <button
                            onClick={() => setActiveTab('data')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'data'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-foreground-muted hover:text-foreground'
                                }`}
                        >
                            <Database className="h-4 w-4 inline mr-2" />
                            {t('projectSettings.data')}
                        </button>
                    </div>

                    {/* General Settings Tab */}
                    {activeTab === 'general' && (
                        <div className="space-y-6">
                            <Card className="p-6">
                                <h2 className="text-lg font-semibold text-foreground mb-4">
                                    {t('projectSettings.projectDetails')}
                                </h2>
                                <form onSubmit={handleSubmit(onSubmitSettings)} className="space-y-4">
                                    <div className="grid gap-2">
                                        <Label htmlFor="name">{t('projectSettings.projectName')}</Label>
                                        <Input
                                            id="name"
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <p className="text-sm text-destructive">
                                                {errors.name.message}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="description">{t('projectSettings.description')}</Label>
                                        <Textarea
                                            id="description"
                                            rows={3}
                                            {...register('description')}
                                        />
                                    </div>

                                    <div className="grid gap-2">
                                        <Label>{t('projectSettings.projectIcon')}</Label>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={iconType === 'emoji' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setIconType('emoji')}
                                                className="flex-1"
                                            >
                                                {t('projectSettings.emoji')}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={iconType === 'image' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setIconType('image')}
                                                className="flex-1"
                                            >
                                                <ImageIcon className="h-4 w-4 mr-2" />
                                                {t('projectSettings.image')}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Icon emoji (shown when emoji type selected) */}
                                    {iconType === 'emoji' && (
                                        <div className="grid gap-2">
                                            <Label>{t('projectSettings.chooseEmoji')}</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {PRESET_EMOJIS.map((emoji) => (
                                                    <button
                                                        key={emoji}
                                                        type="button"
                                                        onClick={() => setSelectedEmoji(emoji)}
                                                        className={cn(
                                                            "h-10 w-10 rounded-lg text-xl flex items-center justify-center transition-all",
                                                            selectedEmoji === emoji
                                                                ? 'bg-primary/20 ring-2 ring-primary'
                                                                : 'bg-surface-hover hover:bg-surface-hover/80'
                                                        )}
                                                    >
                                                        {emoji}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Image upload (shown when image type selected) */}
                                    {iconType === 'image' && (
                                        <div className="grid gap-2">
                                            <Label>{t('projectSettings.uploadImage')}</Label>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileSelect}
                                                className="hidden"
                                            />
                                            {imagePreview ? (
                                                <div className="relative w-20 h-20">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Project icon preview"
                                                        className="w-20 h-20 rounded-lg object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={removeImage}
                                                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="h-20 w-20 rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-1 transition-colors"
                                                >
                                                    <Upload className="h-6 w-6 text-foreground-muted" />
                                                    <span className="text-xs text-foreground-muted">{t('projects.uploadImage')}</span>
                                                </button>
                                            )}
                                            <p className="text-xs text-foreground-muted">
                                                {t('projectSettings.maxSize')}
                                            </p>
                                        </div>
                                    )}

                                    {/* Color picker - only show when emoji type selected */}
                                    {iconType === 'emoji' && (
                                        <div className="grid gap-2">
                                            <Label>{t('projectSettings.color')}</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {PRESET_COLORS.map((color) => (
                                                    <button
                                                        key={color}
                                                        type="button"
                                                        onClick={() => setSelectedColor(color)}
                                                        className={cn(
                                                            "h-8 w-8 rounded-full transition-all",
                                                            selectedColor === color
                                                                ? 'ring-2 ring-offset-2 ring-offset-surface ring-white'
                                                                : ''
                                                        )}
                                                        style={{ backgroundColor: color }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Upload indicator */}
                                    {isUploading && (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span className="text-sm text-foreground-muted">{t('projectSettings.processingImage')}</span>
                                        </div>
                                    )}

                                    <Button type="submit" disabled={updateProject.isPending || isUploading}>
                                        {(updateProject.isPending || isUploading) && (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        )}
                                        {t('projectSettings.saveChanges')}
                                    </Button>
                                </form>
                            </Card>

                            {/* Custom Fields Section */}
                            <Card className="p-6">
                                <CustomFieldManager
                                    projectId={project.id}
                                    canEdit={isOwner || project.role === 'ADMIN'}
                                />
                            </Card>

                            {isOwner && (
                                <Card className="p-6 border-destructive/50">
                                    <h2 className="text-lg font-semibold text-foreground mb-2">
                                        {t('projectSettings.dangerZone')}
                                    </h2>
                                    <p className="text-sm text-foreground-muted mb-4">
                                        {t('projectSettings.dangerZoneDescription')}
                                    </p>
                                    <Button
                                        variant="destructive"
                                        onClick={() => setShowDeleteDialog(true)}
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        {t('projects.deleteProject')}
                                    </Button>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* Integrations Tab */}
                    {activeTab === 'integrations' && (
                        <div className="space-y-6">
                            <Card className="p-6">
                                <div className="flex items-start gap-3 mb-5">
                                    <div className="h-10 w-10 rounded-lg bg-[#5865F2]/20 flex items-center justify-center shrink-0">
                                        <Webhook className="h-5 w-5 text-[#5865F2]" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-foreground">
                                            {t('projectSettings.discordWebhookTitle')}
                                        </h2>
                                        <p className="text-sm text-foreground-muted">
                                            {t('projectSettings.discordWebhookDescription')}
                                        </p>
                                    </div>
                                </div>

                                {discordWebhook?.configured && (
                                    <div className={`mb-5 rounded-lg border p-4 ${discordWebhook.enabled ? 'border-green-500/20 bg-green-500/10' : 'border-amber-500/20 bg-amber-500/10'}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div>
                                                <p className="font-medium text-foreground flex items-center gap-2">
                                                    <Check className={`h-4 w-4 ${discordWebhook.enabled ? 'text-green-500' : 'text-amber-500'}`} />
                                                    {t('projectSettings.webhookConnected')}
                                                </p>
                                                {discordWebhook.webhookName && (
                                                    <p className="text-sm text-foreground-secondary mt-1">{discordWebhook.webhookName}</p>
                                                )}
                                                {discordWebhook.channelId && (
                                                    <p className="text-xs text-foreground-muted mt-1">
                                                        {t('projectSettings.webhookChannel')}: {discordWebhook.channelId}
                                                    </p>
                                                )}
                                                {discordWebhook.lastError && (
                                                    <p className="text-xs text-amber-400 mt-2">{discordWebhook.lastError}</p>
                                                )}
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleTestDiscordWebhook}
                                                    disabled={testDiscordWebhook.isPending}
                                                >
                                                    {testDiscordWebhook.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                                    {t('projectSettings.testWebhook')}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive"
                                                    onClick={handleDeleteDiscordWebhook}
                                                    disabled={deleteDiscordWebhook.isPending}
                                                >
                                                    {deleteDiscordWebhook.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                                    {t('projectSettings.disconnectWebhook')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <div className="grid gap-2">
                                        <Label htmlFor="discord-webhook-url">{t('projectSettings.discordWebhookUrl')}</Label>
                                        <Input
                                            id="discord-webhook-url"
                                            type="password"
                                            autoComplete="off"
                                            value={discordWebhookUrl}
                                            onChange={(event) => setDiscordWebhookUrl(event.target.value)}
                                            placeholder="https://discord.com/api/webhooks/..."
                                        />
                                        <p className="text-xs text-foreground-muted">{t('projectSettings.discordWebhookHint')}</p>
                                    </div>
                                    <Button
                                        onClick={handleSaveDiscordWebhook}
                                        disabled={!discordWebhookUrl.trim() || saveDiscordWebhook.isPending}
                                        className="bg-[#5865F2] hover:bg-[#4752C4]"
                                    >
                                        {saveDiscordWebhook.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        {t('projectSettings.connectWebhook')}
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    )}

                    {/* Import / Export Tab */}
                    {activeTab === 'data' && (
                        <div className="space-y-6">
                            <Card className="p-6">
                                <div className="flex items-start gap-3 mb-6">
                                    <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                                        <Database className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-foreground">
                                            {t('projectSettings.transferTitle')}
                                        </h2>
                                        <p className="text-sm text-foreground-muted">
                                            {t('projectSettings.transferDescription')}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="rounded-lg border border-border bg-surface-hover/30 p-4">
                                        <Download className="h-5 w-5 text-primary mb-3" />
                                        <h3 className="font-medium text-foreground mb-1">
                                            {t('projectSettings.exportTitle')}
                                        </h3>
                                        <p className="text-sm text-foreground-muted mb-4 min-h-10">
                                            {t('projectSettings.exportDescription')}
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={handleExportTasks}
                                            disabled={exportProjectTasks.isPending}
                                        >
                                            {exportProjectTasks.isPending ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Download className="h-4 w-4 mr-2" />
                                            )}
                                            {exportProjectTasks.isPending
                                                ? t('projectSettings.exporting')
                                                : t('projectSettings.exportButton')}
                                        </Button>
                                    </div>

                                    <div className="rounded-lg border border-border bg-surface-hover/30 p-4">
                                        <Upload className="h-5 w-5 text-primary mb-3" />
                                        <h3 className="font-medium text-foreground mb-1">
                                            {t('projectSettings.importTitle')}
                                        </h3>
                                        <p className="text-sm text-foreground-muted mb-4 min-h-10">
                                            {t('projectSettings.importDescription')}
                                        </p>
                                        <input
                                            ref={importFileInputRef}
                                            type="file"
                                            accept=".json,application/json"
                                            className="hidden"
                                            onChange={handleImportFile}
                                        />
                                        <Button
                                            onClick={() => importFileInputRef.current?.click()}
                                            disabled={previewProjectImport.isPending}
                                        >
                                            {previewProjectImport.isPending ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Upload className="h-4 w-4 mr-2" />
                                            )}
                                            {previewProjectImport.isPending
                                                ? t('projectSettings.previewingImport')
                                                : t('projectSettings.importButton')}
                                        </Button>
                                    </div>
                                </div>

                                <p className="mt-4 text-xs text-foreground-muted">
                                    {t('projectSettings.importSafetyHint')}
                                </p>
                            </Card>
                        </div>
                    )}

                    {/* Members Tab */}
                    {activeTab === 'members' && (
                        <div className="space-y-6">
                            <Card className="p-6">
                                <h2 className="text-lg font-semibold text-foreground mb-2">
                                    {t('projectSettings.joinLinkTitle')}
                                </h2>
                                <p className="text-sm text-foreground-muted mb-4">
                                    {t('projectSettings.joinLinkDescription')}
                                </p>
                                {joinLink ? (
                                    <div className="space-y-3">
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Input value={joinLink.joinUrl} readOnly aria-label={t('projectSettings.joinLinkTitle')} />
                                            <Button
                                                variant="outline"
                                                onClick={() => copyInviteLink(joinLink.joinUrl, 'join-link')}
                                            >
                                                {copiedInvite === 'join-link' ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                                                {t('projectSettings.copyLink')}
                                            </Button>
                                        </div>
                                        <p className="text-xs text-foreground-muted">{t('projectSettings.joinLinkWarning')}</p>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={handleGenerateJoinLink}
                                                disabled={generateJoinLink.isPending}
                                            >
                                                {generateJoinLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                                {t('projectSettings.regenerateJoinLink')}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                className="text-destructive hover:text-destructive"
                                                onClick={handleDisableJoinLink}
                                                disabled={disableJoinLink.isPending}
                                            >
                                                {disableJoinLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                                {t('projectSettings.disableJoinLink')}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Button onClick={handleGenerateJoinLink} disabled={generateJoinLink.isPending}>
                                        {generateJoinLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                        {t('projectSettings.createJoinLink')}
                                    </Button>
                                )}
                            </Card>

                            {/* t46 (R14): multi-link invite system (auth required). */}
                            <Card className="p-6">
                                <div className="flex items-start justify-between mb-2 gap-2">
                                    <div>
                                        <h2 className="text-lg font-semibold text-foreground">
                                            {t('inviteLink.sectionTitle')}
                                        </h2>
                                        <p className="text-sm text-foreground-muted">
                                            {t('inviteLink.sectionDescription')}
                                        </p>
                                    </div>
                                    <Button
                                        onClick={() => setShowInviteLinkDialog(true)}
                                        size="sm"
                                    >
                                        {t('inviteLink.create')}
                                    </Button>
                                </div>
                                <InviteLinkList
                                    projectId={projectId || ''}
                                    onCreateClick={() => setShowInviteLinkDialog(true)}
                                />
                                <InviteLinkDialog
                                    projectId={projectId || ''}
                                    open={showInviteLinkDialog}
                                    onOpenChange={setShowInviteLinkDialog}
                                    onCreated={(link: ProjectInviteLink) => {
                                        toast({
                                            title: t('inviteLink.created'),
                                            description: link.inviteUrl,
                                        });
                                    }}
                                />
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold text-foreground">
                                        {t('projectSettings.teamMembers')} ({project.memberCount})
                                    </h2>
                                    <Button onClick={() => setShowInviteDialog(true)}>
                                        <Mail className="h-4 w-4 mr-2" />
                                        {t('projects.invite')}
                                    </Button>
                                </div>

                                <div className="space-y-3">
                                    {/* Owner */}
                                    <div className="flex items-center justify-between py-3 border-b border-border">
                                        <div className="flex items-center gap-3">
                                            <Avatar className="h-10 w-10">
                                                <AvatarImage src={project.owner.avatarUrl || undefined} />
                                                <AvatarFallback className="bg-amber-500 text-white">
                                                    {getInitials(project.owner.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-medium text-foreground">
                                                    {project.owner.name}
                                                    {project.owner.id === user?.id && (
                                                        <span className="text-foreground-muted"> {t('projectSettings.youIndicator')}</span>
                                                    )}
                                                </p>
                                                <p className="text-sm text-foreground-muted">
                                                    {project.owner.email}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${roleBadgeColors.OWNER}`}>
                                            <Crown className="h-3 w-3" />
                                            {t('projects.owner')}
                                        </span>
                                    </div>

                                    {/* Members */}
                                    {members?.filter((m) => m.userId !== project.ownerId).map((member) => {
                                        const RoleIcon = roleIcons[member.role];
                                        const isSelf = member.userId === user?.id;
                                        const canEdit = isOwner || (project.role === 'ADMIN' && member.role !== 'ADMIN');

                                        return (
                                            <div
                                                key={member.id}
                                                className="flex items-center justify-between py-3 border-b border-border last:border-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-10 w-10">
                                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                                        <AvatarFallback className="bg-primary text-primary-foreground">
                                                            {getInitials(member.user.name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium text-foreground">
                                                            {member.user.name}
                                                            {isSelf && (
                                                                <span className="text-foreground-muted"> {t('projectSettings.youIndicator')}</span>
                                                            )}
                                                        </p>
                                                        <p className="text-sm text-foreground-muted">
                                                            {member.user.email}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {canEdit && !isSelf ? (
                                                        <Select
                                                            value={member.role}
                                                            onValueChange={(value) =>
                                                                handleRoleChange(member.id, value as ProjectRole)
                                                            }
                                                        >
                                                            <SelectTrigger className="w-32">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {isOwner && (
                                                                    <SelectItem value="ADMIN">{t('projects.admin')}</SelectItem>
                                                                )}
                                                                <SelectItem value="MEMBER">{t('projects.member')}</SelectItem>
                                                                <SelectItem value="VIEWER">{t('projects.viewer')}</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${roleBadgeColors[member.role]}`}>
                                                            <RoleIcon className="h-3 w-3" />
                                                            {getRoleLabel(member.role, t)}
                                                        </span>
                                                    )}
                                                    {(canEdit || isSelf) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-foreground-muted hover:text-destructive"
                                                            onClick={() => handleRemoveMember(member.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>

                            {/* Pending Invitations */}
                            {invitations && invitations.length > 0 && (
                                <Card className="p-6">
                                    <h2 className="text-lg font-semibold text-foreground mb-4">
                                        {t('projectSettings.pendingInvitations')} ({invitations.length})
                                    </h2>
                                    <div className="space-y-3">
                                        {invitations.map((invite) => (
                                            <div
                                                key={invite.id}
                                                className="flex items-center justify-between py-3 border-b border-border last:border-0"
                                            >
                                                <div>
                                                    <p className="font-medium text-foreground">
                                                        {invite.email}
                                                    </p>
                                                    <p className="text-sm text-foreground-muted">
                                                        {t('projectSettings.role')}: {getRoleLabel(invite.role, t)} • {t('projects.expires')}{' '}
                                                        {new Date(invite.expiresAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => copyInviteLink(invite.inviteUrl, invite.id)}
                                                    >
                                                        {copiedInvite === invite.id ? (
                                                            <Check className="h-4 w-4 mr-1" />
                                                        ) : (
                                                            <Copy className="h-4 w-4 mr-1" />
                                                        )}
                                                        {t('projectSettings.copyLink')}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-foreground-muted hover:text-destructive"
                                                        onClick={() => handleCancelInvite(invite.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Invite Dialog */}
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                <DialogContent>
                    <form onSubmit={handleInviteSubmit(onSubmitInvite)}>
                        <DialogHeader>
                            <DialogTitle>{t('projectSettings.inviteTeamMember')}</DialogTitle>
                            <DialogDescription>
                                {t('projectSettings.inviteDescription')}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="invite-email">{t('projectSettings.emailAddress')}</Label>
                                <Input
                                    id="invite-email"
                                    type="email"
                                    placeholder={t('projectSettings.emailPlaceholder')}
                                    {...registerInvite('email')}
                                />
                                {inviteErrors.email && (
                                    <p className="text-sm text-destructive">
                                        {inviteErrors.email.message}
                                    </p>
                                )}
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="invite-role">{t('projectSettings.role')}</Label>
                                <Select
                                    value={watchInvite('role')}
                                    onValueChange={(value) =>
                                        setInviteValue('role', value as 'ADMIN' | 'MEMBER' | 'VIEWER')
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {isOwner && <SelectItem value="ADMIN">{t('projects.admin')}</SelectItem>}
                                        <SelectItem value="MEMBER">{t('projects.member')}</SelectItem>
                                        <SelectItem value="VIEWER">{t('projects.viewer')}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-foreground-muted">
                                    {watchInvite('role') === 'ADMIN' &&
                                        t('projectSettings.roleAdminDesc')}
                                    {watchInvite('role') === 'MEMBER' &&
                                        t('projectSettings.roleMemberDesc')}
                                    {watchInvite('role') === 'VIEWER' &&
                                        t('projectSettings.roleViewerDesc')}
                                </p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowInviteDialog(false)}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={createInvitation.isPending}>
                                {createInvitation.isPending && (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                )}
                                {t('projectSettings.sendInvitation')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Import Preview Dialog */}
            <Dialog
                open={showImportPreview}
                onOpenChange={(open) => {
                    if (!importProjectTasks.isPending) setShowImportPreview(open);
                }}
            >
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{t('projectSettings.importPreviewTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('projectSettings.importPreviewDescription').replace('{file}', importFileName)}
                        </DialogDescription>
                    </DialogHeader>

                    {importPreview && (
                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                                {[
                                    [t('projectSettings.tasksToCreate'), importPreview.tasksCreated],
                                    [t('projectSettings.tasksToUpdate'), importPreview.tasksUpdated],
                                    [t('projectSettings.tasksToDelete'), importPreview.tasksDeleted],
                                    [t('projectSettings.fieldsToCreate'), importPreview.customFieldsCreated],
                                    [t('projectSettings.fieldsToUpdate'), importPreview.customFieldsUpdated],
                                    [t('projectSettings.optionsToAdd'), importPreview.optionsAdded],
                                ].map(([label, value]) => (
                                    <div key={String(label)} className="rounded-lg border border-border bg-surface-hover/30 p-3">
                                        <p className="text-2xl font-semibold text-foreground">{value}</p>
                                        <p className="text-xs text-foreground-muted">{label}</p>
                                    </div>
                                ))}
                            </div>

                            {importPreview.warnings.length > 0 && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                                    <p className="flex items-center gap-2 text-sm font-medium text-amber-300 mb-2">
                                        <AlertTriangle className="h-4 w-4" />
                                        {t('projectSettings.importWarnings')} ({importPreview.warnings.length})
                                    </p>
                                    <ul className="max-h-40 overflow-y-auto space-y-1 text-xs text-foreground-secondary list-disc pl-5">
                                        {importPreview.warnings.map((warning) => (
                                            <li key={warning}>{warning}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <p className="text-xs text-foreground-muted">
                                {t('projectSettings.importSafetyHint')}
                            </p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowImportPreview(false)}
                            disabled={importProjectTasks.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleConfirmImport} disabled={!importPreview || importProjectTasks.isPending}>
                            {importProjectTasks.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {importProjectTasks.isPending
                                ? t('projectSettings.importing')
                                : t('projectSettings.confirmImport')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('projectSettings.deleteProjectTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('projectSettings.deleteProjectDescription').replace('{name}', project.name)}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteProject.isPending}
                        >
                            {deleteProject.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('projects.deleteProject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppLayout>
    );
}
